import {Button, DataStatus, Separator} from 'platform/components';
import {Box, Heading, Hide, HStack, Scroll, Show, VStack} from 'platform/foundation';

import {isNilOrEmpty} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {PlainBasket, useBasketItems} from '@dms/shared';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import {AfterSalesPlainMaterialBasketItem} from './components/AfterSalesPlainMaterialBasketItem';
import {PlainMaterialBasketItem} from './types/PlainMaterialBasketItem';

const BASKET_TITLE_HEIGHT_IN_REM = 3.5;

export interface AfterSalesPlainMaterialBasketProps<T extends PlainMaterialBasketItem>
  extends RequiredTestIdProps {
  basket: PlainBasket<T>;
  onDelete: (itemsIds: string[]) => Promise<void>;
}

/**
 *
 * AfterSalesPlainMaterialBasket is used only to show necessary details like catalog number, manufacturer name and allows only to delete items.
 * For advanced usage of material basket, use AfterSalesMaterialBasket which allows to show prices, quantity and actions like tooltip, edit, delete.
 *
 */
export function AfterSalesPlainMaterialBasket<T extends PlainMaterialBasketItem>(
  props: AfterSalesPlainMaterialBasketProps<T>
) {
  const {
    basketItems,
    basketItemsCount,
    hasSomeBasketItemsSelected,
    hasAllBasketItemsSelected,
    onSelectBasketItem,
    onSelectAllBasketItems,
    onBulkDeleteBasketItems,
  } = useBasketItems({basketItems: props.basket?.items, onDeleteBasketItems: props.onDelete});

  const basketTitle = `${i18n.t('general.labels.addedItems')} (${basketItemsCount})`;

  return (
    <Box
      borderRadius="small"
      boxShadow="elevation_1"
      backgroundColor="palettes.white.10.100"
      height="100%"
      data-testid={suffixTestId('wrapper', props)}
    >
      <VStack height="100%">
        <Box padding={4}>
          <Heading size={4} data-testid={suffixTestId('basketTitle', props)}>
            {basketTitle}
          </Heading>
        </Box>
        <Separator spacing={0} />
        <Box padding={4} height="100%" maxHeight={`calc(100% - ${BASKET_TITLE_HEIGHT_IN_REM}rem)`}>
          <VStack height="100%" spacing={3}>
            <HStack justify="space-between">
              <Hide when={isNilOrEmpty(basketItems)}>
                <Button
                  type="button"
                  variant="link"
                  leftIcon="action/done_all"
                  title={
                    hasAllBasketItemsSelected
                      ? i18n.t('general.actions.unselectAll')
                      : i18n.t('general.actions.selectAll')
                  }
                  onClick={onSelectAllBasketItems}
                  data-testid={suffixTestId('actions.toggleSelect', props)}
                />
              </Hide>
              <Show when={hasSomeBasketItemsSelected}>
                <Button
                  type="button"
                  variant="dangerLink"
                  leftIcon="navigation/close"
                  title={i18n.t('general.actions.remove')}
                  isLoading={props.basket.isDeletingItems}
                  onClick={onBulkDeleteBasketItems}
                  data-testid={suffixTestId('actions.remove', props)}
                />
              </Show>
            </HStack>
            <Scroll flex={1} auto>
              <VStack height="100%" spacing={4}>
                <DataStatus
                  isLoading={props.basket.isLoading}
                  isError={props.basket.hasError}
                  isEmpty={basketItemsCount === 0}
                  emptyMessage={i18n.t('general.notifications.noItemsSelected')}
                  minHeight="100%"
                >
                  {basketItems?.map((basketItem, index) => (
                    <AfterSalesPlainMaterialBasketItem
                      key={basketItem.id}
                      item={basketItem}
                      onSelect={onSelectBasketItem}
                      onDelete={props.onDelete}
                      data-testid={suffixTestId(`basketItem-[${index}]`, props)}
                    />
                  ))}
                </DataStatus>
              </VStack>
            </Scroll>
          </VStack>
        </Box>
      </VStack>
    </Box>
  );
}
